import {
	Button,
	ControlledInput,
	DateInputComponent,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { ReceiptPaymentTypeEnum } from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { PlusIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { useCreateReceiptPaymentMutation } from "../../../api/queries/receipt.queries";
import { ControlledPriceInput } from "../../../components/inputs/ControlledPriceInput";

const normalizeDecimalText = (value: string): string => {
	const normalized = value.replace(",", ".");
	return normalized;
};

const createPaymentFormSchema = z.object({
	paymentDate: z
		.custom<string>()
		.refine((val) => val !== undefined && val !== "", {
			message: "La date de règlement est requise",
		}),
	amount: z
		.string()
		.min(1, "Le montant est requis")
		.refine(
			(value) => {
				const normalized = normalizeDecimalText(value);
				return !Number.isNaN(Number(normalized)) && Number(normalized) > 0;
			},
			{ message: "Le montant doit être un nombre positif" },
		),
	type: z.enum(ReceiptPaymentTypeEnum, {
		error: "Le type de paiement est requis",
	}),
	paymentReference: z.string().min(1, "La référence de paiement est requise"),
});

type CreatePaymentFormSchema = z.infer<typeof createPaymentFormSchema>;

const DefaultValues: CreatePaymentFormSchema = {
	paymentDate: today(getLocalTimeZone()).toString(),
	amount: "0",
	type: ReceiptPaymentTypeEnum.TRANSFER,
	paymentReference: "",
};

interface CreatePaymentReceiptModalProps {
	receiptId: number;
	onSuccess?: () => void;
}

const CreatePaymentReceiptModal = ({
	receiptId,
	onSuccess,
}: CreatePaymentReceiptModalProps) => {
	const [open, setOpen] = useState(false);
	const createPaymentMutation = useCreateReceiptPaymentMutation();

	const form = useForm<CreatePaymentFormSchema>({
		resolver: zodResolver(createPaymentFormSchema),
		defaultValues: DefaultValues,
		mode: "onChange",
	});

	const handleOpenChange = useCallback(
		(newOpen: boolean) => {
			setOpen(newOpen);
			if (!newOpen) {
				form.reset(DefaultValues);
			}
		},
		[form],
	);

	const onSubmit = useCallback(
		async (data: CreatePaymentFormSchema) => {
			try {
				await createPaymentMutation.mutateAsync({
					receiptId,
					paymentDate: data.paymentDate,
					amount: Number(normalizeDecimalText(data.amount)),
					type: data.type,
					paymentReference: data.paymentReference,
				});

				toast.success("Le règlement a été ajouté avec succès");

				handleOpenChange(false);
				onSuccess?.();
			} catch (error) {
				console.error("Erreur lors de la création du règlement:", error);
				toast.error("Une erreur est survenue lors de l'ajout du règlement");
			}
		},
		[createPaymentMutation, receiptId, handleOpenChange, onSuccess],
	);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<Button onClick={() => setOpen(true)}>
				<PlusIcon />
				Ajouter un règlement
			</Button>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Ajouter un règlement</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="paymentDate"
								render={({ field }) => (
									<FormItem className="col-span-2">
										<FormLabel>Date de règlement</FormLabel>
										<FormControl>
											<DateInputComponent
												value={field.value ? parseDate(field.value) : undefined}
												onChange={(value) => {
													field.onChange(value?.toString() || "");
												}}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<ControlledPriceInput
								control={form.control}
								name="amount"
								label="Montant"
								iconOrText="€"
							/>

							<FormField
								control={form.control}
								name="type"
								render={({ field }) => (
									<FormItem className="col-span-2">
										<FormLabel>Type de paiement</FormLabel>
										<FormControl>
											<Select
												value={field.value}
												onValueChange={field.onChange}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner un type" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={ReceiptPaymentTypeEnum.CHECK}>
														Chèque
													</SelectItem>
													<SelectItem value={ReceiptPaymentTypeEnum.TRANSFER}>
														Virement
													</SelectItem>
												</SelectContent>
											</Select>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<ControlledInput
								control={form.control}
								name="paymentReference"
								label="Référence du paiement"
							/>
						</div>

						<DialogFooter className="mt-8 w-full sm:!justify-between">
							<Button
								type="button"
								variant="outline"
								className="flex-1"
								onClick={() => handleOpenChange(false)}
							>
								Annuler
							</Button>
							<Button
								type="submit"
								className="flex-1"
								disabled={createPaymentMutation.isPending}
								isLoading={createPaymentMutation.isPending}
							>
								{createPaymentMutation.isPending ? "Création..." : "Valider"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default CreatePaymentReceiptModal;
